import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export interface CommandPaletteContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** For the dialog's `onOpenChange`. */
  setOpen: (open: boolean) => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | undefined>(undefined);

/** Cmd+K on macOS, Ctrl+K elsewhere; no other modifier, no key repeat. */
function isPaletteShortcut(event: KeyboardEvent): boolean {
  return (
    (event.metaKey || event.ctrlKey) &&
    !event.altKey &&
    !event.shiftKey &&
    !event.repeat &&
    event.key.toLowerCase() === 'k'
  );
}

/** True while a Radix dialog, sheet, alert dialog or popover (all `role="dialog"`) is open. */
function isModalOpen(): boolean {
  return document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]') !== null;
}

/**
 * CommandPaletteProvider owns the open state of the command palette and the
 * global Cmd/Ctrl+K shortcut that toggles it. The shortcut stands down while
 * another dialog is open, so it never stacks the palette over a form in a
 * dialog. Mount it once around the app shell; `<CommandPalette />` and any
 * trigger read it via `useCommandPalette`.
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isPaletteShortcut(event)) return;
      // The open palette is a dialog itself; only other dialogs block the shortcut.
      if (!isOpenRef.current && isModalOpen()) return;
      // The browser binds Ctrl+K to its own search box.
      event.preventDefault();
      setOpen((current) => !current);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const value = useMemo(() => ({ isOpen, open, close, setOpen }), [isOpen, open, close]);

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}

/** The command palette state. Throws outside a `CommandPaletteProvider`. */
export function useCommandPalette(): CommandPaletteContextValue {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error('useCommandPalette must be used within CommandPaletteProvider');
  }
  return context;
}
